import type { ServerCreationOptions, ServerRequestOptions } from "../lib/webauthn";
import { api as defaultClient, type ApiClient, type RequestOptions } from "./client";
import type * as S from "./staffTypes";
import type * as T from "./types";

// Every core-platform route the portals call, one function each. Route
// registrations: cmd/core-platform/{auth,webauthn,advertiser,publisher,
// statements}.go (grep `HandleFunc("`). Nothing here invents a route —
// endpoints that don't exist yet live in gaps.ts as typed stubs.

const enc = encodeURIComponent;

/** "?a=1&b=2" from the params that are set, or "" (the first page of a list sends no query). */
function qs(params: Record<string, string | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) u.set(k, v);
  const s = u.toString();
  return s ? `?${s}` : "";
}

export function endpoints(c: ApiClient = defaultClient) {
  const noKey: RequestOptions = { idempotencyKey: null };
  const recoveryOpts: RequestOptions = { idempotencyKey: null, noStepUp: true };
  return {
    auth: {
      /** auth.go:26 */
      signup: (req: T.SignupRequest) => c.post<T.SignupResponse>("/v1/auth/signup", req, noKey),
      /** auth.go:27 */
      login: (req: T.LoginRequest) => c.post<T.LoginResponse>("/v1/auth/login", req, noKey),
      /** auth.go:28 — TOTP code or recovery code against the login MFA challenge. */
      verifyMfa: (req: T.MfaVerifyRequest) => c.post<T.CodeResponse>("/v1/auth/mfa/verify", req, noKey),
      /** auth.go:29 — sets the vads_session cookie, returns the CSRF token. */
      exchangeToken: (req: T.TokenExchangeRequest) =>
        c.post<T.TokenExchangeResponse>("/v1/auth/token", req, noKey),
      /** auth.go:30 */
      logout: () => c.post<{ status: string }>("/v1/auth/logout", undefined, noKey),
      /** account.go:55 (0055) — party type, legal name, email, MFA status and freshness. */
      me: (opts?: RequestOptions) => c.get<T.MeResponse>("/v1/me", opts),
      /**
       * account.go:56 (0055, G-02) — the session's CSRF token, for a tab that
       * has the cookie but no token (a new tab, a restored tab). `quiet`: a
       * 401 here just means "not signed in", not "your session was lost".
       */
      session: () => c.get<T.SessionReissueResponse>("/v1/auth/session", { quiet: true }),
      /** account.go:57 (0055, G-05). */
      mfaFactors: () => c.get<T.MfaFactors>("/v1/auth/mfa/factors"),
      /**
       * account.go:58 (0055, G-05) — RequireRecentMFA; the interceptor handles
       * the step-up. Not replayable server-side: every call is a new batch.
       */
      regenerateRecoveryCodes: () => c.post<T.RecoveryCodesResponse>("/v1/auth/mfa/recovery-codes", undefined, noKey),
      /** auth.go:31 — step-up required if another factor already exists (mfa.go:21-26). */
      enrollTotp: (email: string) => c.post<T.MfaEnrollResponse>("/v1/auth/mfa/enroll", { email }, noKey),
      /** auth.go:32 — returns one-time recovery codes. */
      confirmTotp: (code: string) => c.post<T.MfaConfirmResponse>("/v1/auth/mfa/confirm", { code }, noKey),
      /** auth.go:36 — TOTP step-up (stamps mfa_verified_at). */
      stepUpTotp: (code: string) =>
        c.post<{ status: string }>("/v1/auth/mfa/step-up", { code }, { idempotencyKey: null, noStepUp: true }),
      // Account recovery — cmd/core-platform/recovery.go (registerRecoveryRoutes).
      // No session or CSRF: the user is locked out. Every route is rate
      // limited (429 rate_limited + Retry-After).
      recovery: {
        /** Always 202 {"status":"accepted"}, whether or not the account exists. */
        requestPasswordReset: (email: string) =>
          c.post<T.RecoveryAccepted>("/v1/auth/password-reset/request", { email }, recoveryOpts),
        /** 200 | 400 recovery_token_invalid | 400 password_policy {reason}. */
        completePasswordReset: (token: string, newPassword: string) =>
          c.post<{ status: "password_reset" }>(
            "/v1/auth/password-reset/complete",
            { token, new_password: newPassword },
            recoveryOpts,
          ),
        /** Always 202 {"status":"accepted"}, whether or not the account exists. */
        requestMfaReset: (email: string) =>
          c.post<T.RecoveryAccepted>("/v1/auth/mfa-reset/request", { email }, recoveryOpts),
        /** The code sent to the recovery contact. 200 | 400 recovery_token_invalid. */
        confirmContact: (code: string) =>
          c.post<{ status: "contact_confirmed" }>("/v1/auth/mfa-reset/confirm-contact", { code }, recoveryOpts),
        /** The token from the account-email link. 200 | 400 recovery_token_invalid | 409 recovery_contact_unconfirmed. */
        completeMfaReset: (token: string) =>
          c.post<{ status: "mfa_reset" }>("/v1/auth/mfa-reset/complete", { token }, recoveryOpts),
      },
      webauthn: {
        /** webauthn.go:45 */
        registerBegin: (label: string) =>
          c.post<{ options: ServerCreationOptions }>("/v1/auth/mfa/webauthn/register/begin", { label }, noKey),
        /** webauthn.go:46 */
        registerFinish: (credential: Record<string, unknown>) =>
          c.post<T.WebAuthnRegisterFinishResponse>("/v1/auth/mfa/webauthn/register/finish", { credential }, noKey),
        /** webauthn.go:47 — pre-session, bound to the login mfa_challenge. */
        loginBegin: (mfaChallenge: string) =>
          c.post<{ options: ServerRequestOptions }>(
            "/v1/auth/mfa/webauthn/login/begin",
            { mfa_challenge: mfaChallenge },
            noKey,
          ),
        /** webauthn.go:48 */
        loginFinish: (mfaChallenge: string, credential: Record<string, unknown>) =>
          c.post<T.CodeResponse>(
            "/v1/auth/mfa/webauthn/login/finish",
            { mfa_challenge: mfaChallenge, credential },
            noKey,
          ),
        /** webauthn.go:49 */
        stepUpBegin: () =>
          c.post<{ options: ServerRequestOptions }>("/v1/auth/mfa/webauthn/step-up/begin", undefined, {
            idempotencyKey: null,
            noStepUp: true,
          }),
        /** webauthn.go:50 */
        stepUpFinish: (credential: Record<string, unknown>) =>
          c.post<{ status: string }>(
            "/v1/auth/mfa/webauthn/step-up/finish",
            { credential },
            { idempotencyKey: null, noStepUp: true },
          ),
        /** webauthn.go:51 */
        list: () => c.get<{ credentials: T.WebAuthnCredentialInfo[] }>("/v1/auth/mfa/webauthn/credentials"),
        /** webauthn.go:52 — RequireRecentMFA. */
        remove: (id: string) => c.delete<{ status: string }>(`/v1/auth/mfa/webauthn/credentials/${enc(id)}`),
      },
    },

    // Advertiser routes: cmd/core-platform/advertiser.go registerAdvertiserRoutes
    // (master 0052/0054; wallet and refund reads from 0056, branch
    // worktree-agent-aad451b457c83342c, advertiser.go:60-75 there).
    advertiser: {
      /** advertiser.go:27 — GET /v1/campaigns?cursor= (campaign_read.go:47). */
      listCampaigns: (cursor?: string | null) => c.get<T.CampaignList>(`/v1/campaigns${qs({ cursor })}`),
      /** advertiser.go:28 — campaign_read.go:101. */
      getCampaign: (campaignId: string) => c.get<T.CampaignDetail>(`/v1/campaigns/${enc(campaignId)}`),
      /** advertiser.go:29 — campaign_read.go:131. 404 terms_unavailable_for_currency. */
      getCurrentTerms: (currency: string) =>
        c.get<T.CurrentTerms>(`/v1/commercial-terms/current${qs({ currency })}`),
      /** advertiser.go:30 — campaign_commands.go:295. */
      createCampaign: (req: T.CreateCampaignRequest, opts?: RequestOptions) =>
        c.post<T.CreateCampaignResponse>("/v1/campaigns", req, opts),
      /** advertiser.go:31 — campaign_commands.go:343. */
      renameCampaign: (campaignId: string, name: string, opts?: RequestOptions) =>
        c.patch<T.RenameCampaignResponse>(`/v1/campaigns/${enc(campaignId)}`, { name }, opts),
      /** advertiser.go:32 — campaign_commands.go:386. Step-up once out of Draft. */
      updateSchedule: (campaignId: string, req: T.UpdateScheduleRequest, opts?: RequestOptions) =>
        c.patch<T.UpdateScheduleResponse>(`/v1/campaigns/${enc(campaignId)}/schedule`, req, opts),
      /** advertiser.go:33 — campaign_commands.go:761. Step-up on an increase. */
      updateBudget: (campaignId: string, req: T.UpdateBudgetRequest, opts?: RequestOptions) =>
        c.patch<T.UpdateBudgetResponse>(`/v1/campaigns/${enc(campaignId)}/budget`, req, opts),
      /** advertiser.go:34 — terminal. */
      endCampaign: (campaignId: string, opts?: RequestOptions) =>
        c.post<T.EndCampaignResponse>(`/v1/campaigns/${enc(campaignId)}/end`, undefined, opts),
      /** advertiser.go:35 — campaign_commands.go:452. */
      createConfigVersion: (campaignId: string, req: T.CreateConfigVersionRequest, opts?: RequestOptions) =>
        c.post<T.CreateConfigVersionResponse>(`/v1/campaigns/${enc(campaignId)}/config-versions`, req, opts),
      /** advertiser.go:36 — campaign_commands.go:535. */
      createCreativeVersion: (campaignId: string, req: T.CreateCreativeVersionRequest, opts?: RequestOptions) =>
        c.post<T.CreateCreativeVersionResponse>(`/v1/campaigns/${enc(campaignId)}/creative-versions`, req, opts),
      /** advertiser.go:37 — campaign_commands.go:607. Version-pinned: the body is required. */
      acceptTerms: (campaignId: string, req: T.AcceptTermsRequest, opts?: RequestOptions) =>
        c.post<T.AcceptTermsResponse>(`/v1/campaigns/${enc(campaignId)}/terms/accept`, req, opts),
      /** advertiser.go:45 */
      getCreativeDomainChallenge: (campaignId: string, creativeVersionId: string) =>
        c.get<T.DomainChallenge>(
          `/v1/campaigns/${enc(campaignId)}/creative-versions/${enc(creativeVersionId)}/domain-verification`,
        ),
      /** advertiser.go:47 */
      verifyCreativeDomain: (campaignId: string, creativeVersionId: string) =>
        c.post<T.DomainVerifyResponse>(
          `/v1/campaigns/${enc(campaignId)}/creative-versions/${enc(creativeVersionId)}/domain-verification`,
        ),
      // Migration 0061 (advertiser.go:42-48). GET /v1/targeting/categories
      // moved to the shared `targeting` group below (also publisher-readable
      // since the web-reported fix).
      /** advertiser.go:43 — targeting.go:68. `q` must be 2-253 characters (400 otherwise). */
      searchTargetingPublishers: (q: string) =>
        c.get<T.TargetingPublisherList>(`/v1/targeting/publishers${qs({ q })}`),
      /** advertiser.go:44 — targeting.go:111. At most 64 categories. */
      getTargetingInventory: (categories: string[]) =>
        c.get<T.TargetingInventory>(`/v1/targeting/inventory${qs({ categories: categories.join(",") })}`),
      /** advertiser.go:45 — creative_validate.go:442. */
      listCreativeFormats: () => c.get<T.CreativeFormatList>("/v1/creative-formats"),
      /** advertiser.go:46 — creative_validate.go:424. Read-only, so no idempotency key. */
      validateCreative: (req: T.CreativeValidationRequest, opts?: RequestOptions) =>
        c.post<T.CreativeValidation>("/v1/creatives/validate", req, { ...opts, idempotencyKey: null }),
      /** advertiser.go:47 — candidates.go:43. Send pending_candidate exactly as shown. */
      withdrawCandidate: (campaignId: string, pair: T.CandidatePair, opts?: RequestOptions) =>
        c.post<T.WithdrawCandidateResponse>(`/v1/campaigns/${enc(campaignId)}/candidates/withdraw`, pair, opts),
      /** advertiser.go:48 — candidates.go:93. 201. */
      appealVersion: (campaignId: string, versionId: string, basis: string, opts?: RequestOptions) =>
        c.post<T.AppealVersionResponse>(
          `/v1/campaigns/${enc(campaignId)}/versions/${enc(versionId)}/appeal`,
          { basis },
          opts,
        ),
      /** advertiser.go:54 — campaign_commands.go:659. */
      promoteRelease: (campaignId: string, req: T.PromoteReleaseRequest, opts?: RequestOptions) =>
        c.post<T.PromoteReleaseResponse>(`/v1/campaigns/${enc(campaignId)}/release`, req, opts),
      /** advertiser.go:73 */
      pauseCampaign: (campaignId: string, opts?: RequestOptions) =>
        c.post<T.PauseCampaignResponse>(`/v1/campaigns/${enc(campaignId)}/pause`, undefined, opts),
      /** advertiser.go:74 — a blocked resume is 422 {error: <reason>, result: "resume_blocked"}. */
      resumeCampaign: (campaignId: string, opts?: RequestOptions) =>
        c.post<T.ResumeCampaignResponse>(`/v1/campaigns/${enc(campaignId)}/resume`, undefined, opts),
      /** report_snapshot.go:234 — reporting.go's live ledger read model. */
      getCampaignReport: (campaignId: string) => c.get<T.CampaignReport>(`/v1/campaigns/${enc(campaignId)}/report`),
      /** advertiser.go:84 — campaign_report_extra.go:92. Default window: last 30 days. */
      getCampaignShadowReport: (campaignId: string) =>
        c.get<T.CampaignShadowReport>(`/v1/campaigns/${enc(campaignId)}/report/shadow`),
      /** advertiser.go:85 — campaign_report_extra.go:199. */
      getCampaignPeriodReport: (campaignId: string, timezone?: string) =>
        c.get<T.CampaignPeriodReport>(`/v1/campaigns/${enc(campaignId)}/report/periods${qs({ timezone })}`),

      /** POST /v1/wallet/deposits/intent — wallet.go:66 (0056). Returns checkout_session_id too. */
      createDepositIntent: (amount: string, idempotencyKey: string) =>
        c.post<T.DepositIntentResponse>("/v1/wallet/deposits/intent", { amount }, { idempotencyKey }),
      /** GET /v1/wallet — wallet.go:212 (0056). */
      getWallet: (opts?: RequestOptions) => c.get<T.WalletSummary>("/v1/wallet", opts),
      /** GET /v1/wallet/promo-credit — promotions.go (0075). Promo credit, separate from the cash balances. */
      getPromoCredit: () => c.get<T.PromoCreditSummary>("/v1/wallet/promo-credit"),
      /** POST /v1/wallet/promo-codes — promotions.go (0075). Applies to the NEXT qualifying deposit. */
      redeemPromoCode: (code: string) => c.post<T.RedeemPromoCodeResponse>("/v1/wallet/promo-codes", { code }, noKey),
      /** GET /v1/wallet/deposits?cursor= — wallet.go:228 (0056). */
      listDeposits: (cursor?: string | null) => c.get<T.DepositList>(`/v1/wallet/deposits${qs({ cursor })}`),
      /** GET /v1/wallet/deposits/{id}/refundable — wallet.go:256 (0056). */
      getRefundable: (depositId: string) =>
        c.get<T.RefundableFigures>(`/v1/wallet/deposits/${enc(depositId)}/refundable`),
      /** GET /v1/wallet/deposits/checkout/{session_id} — wallet.go:261-270 (0056). */
      getCheckoutSession: (sessionId: string) =>
        c.get<T.CheckoutSessionStatus>(`/v1/wallet/deposits/checkout/${enc(sessionId)}`),
      /** POST /v1/refunds — refunds.go:51 (0056). RequireRecentMFA. */
      submitRefund: (sourceDepositId: string, amount: string, idempotencyKey: string, reason?: string) =>
        c.post<T.SubmitRefundResponse>(
          "/v1/refunds",
          reason ? { source_deposit_id: sourceDepositId, amount, reason } : { source_deposit_id: sourceDepositId, amount },
          { idempotencyKey },
        ),
      /** GET /v1/refunds?cursor=&deposit_id= — refunds.go:150 (0056). */
      listRefunds: (cursor?: string | null, depositId?: string | null) =>
        c.get<T.RefundList>(`/v1/refunds${qs({ cursor, deposit_id: depositId })}`),
      /** GET /v1/refunds/{id} — refunds.go:130 (0056). */
      getRefund: (id: string) => c.get<T.RefundDetail>(`/v1/refunds/${enc(id)}`),
      /**
       * POST /v1/receivables/{case}/payment-intent — receivable_collection.go
       * (migration 0067). RequireSession + requireAdvertiser; matrix row
       * 'spend'. amount omitted pays the whole outstanding balance. 404
       * receivable_not_found; 422 receivable_not_collectible {state, reason},
       * nothing_outstanding, invalid_amount, amount_exceeds_outstanding
       * {requested, outstanding, currency}; 503 payments_not_configured.
       */
      payReceivable: (caseId: string, amount: string | undefined, idempotencyKey: string) =>
        c.post<T.ReceivablePaymentIntentResponse>(
          `/v1/receivables/${enc(caseId)}/payment-intent`,
          amount ? { amount } : {},
          { idempotencyKey },
        ),
    },

    // Publisher routes: cmd/core-platform/publisher.go registerPublisherRoutes
    // and report_snapshot.go (migration 0057, branch
    // worktree-agent-ac4ee0af51ed82712; publisher.go:33-71 there).
    publisher: {
      /** publisher.go:39 — publisher_reads.go:98. */
      getProfile: () => c.get<T.PublisherProfile>("/v1/publisher"),
      /** publisher.go:37 — publisher_commands.go:444. Only legal_name; locked once KYC starts. */
      updateProfile: (legalName: string, opts?: RequestOptions) =>
        c.patch<T.UpdatePublisherResponse>("/v1/publisher", { legal_name: legalName }, opts),
      /** publisher.go:40 — publisher_reads.go:180. */
      listSites: (cursor?: string | null) => c.get<T.SiteList>(`/v1/sites${qs({ cursor })}`),
      /** publisher.go:41 — publisher_reads.go:253. */
      getSite: (siteId: string) => c.get<T.SiteDetail>(`/v1/sites/${enc(siteId)}`),
      /** publisher.go:33 — publisher_commands.go:211. */
      createSite: (domain: string, opts?: RequestOptions) => c.post<T.CreateSiteResponse>("/v1/sites", { domain }, opts),
      /** publisher.go:42 — publisher_reads.go:362. */
      listSlots: (siteId: string, cursor?: string | null) =>
        c.get<T.SlotList>(`/v1/sites/${enc(siteId)}/slots${qs({ cursor })}`),
      /** publisher.go:35 — publisher_commands.go:262. */
      createSlot: (siteId: string, req: T.CreateSlotRequest, opts?: RequestOptions) =>
        c.post<T.CreateSlotResponse>(`/v1/sites/${enc(siteId)}/slots`, req, opts),
      /** publisher.go:43 — publisher_reads.go:470. */
      listApiKeys: (siteId: string, cursor?: string | null) =>
        c.get<T.ApiKeyList>(`/v1/sites/${enc(siteId)}/api-keys${qs({ cursor })}`),
      /** publisher.go:34 — publisher_commands.go:313. Only "live" exists. */
      createApiKey: (siteId: string, opts?: RequestOptions) =>
        c.post<T.CreateApiKeyResponse>(`/v1/sites/${enc(siteId)}/api-keys`, { environment: "live" }, opts),
      /** publisher.go:36 — publisher_commands.go:400. */
      revokeApiKey: (apiKeyId: string, reason: T.RevocationReason, opts?: RequestOptions) =>
        c.post<T.RevokeApiKeyResponse>(`/v1/api-keys/${enc(apiKeyId)}/revoke`, { reason }, opts),
      /** publisher.go:55 */
      getSiteDomainChallenge: (siteId: string) =>
        c.get<T.DomainChallenge>(`/v1/sites/${enc(siteId)}/domain-verification`),
      /** publisher.go:56 */
      verifySiteDomain: (siteId: string) =>
        c.post<T.DomainVerifyResponse>(`/v1/sites/${enc(siteId)}/domain-verification`),
      /** publisher.go:66 — publisher_commands.go:515. RequireRecentMFA; idempotent since 0057. */
      createConnectOnboardingLink: (opts?: RequestOptions) =>
        c.post<T.ConnectOnboardingLink>("/v1/payouts/connect-account", undefined, opts),
      /** publisher.go:67 */
      getConnectAccountStatus: (opts?: RequestOptions) =>
        c.get<T.ConnectAccountStatus>("/v1/payouts/connect-account", opts),
      /** publisher.go:70 — publisher_commands.go:622. RequireRecentMFA. */
      requestPayout: (amount: string, siteIds: string[], idempotencyKey: string) =>
        c.post<T.RequestPayoutResponse>("/v1/payouts/request", { amount, site_ids: siteIds }, { idempotencyKey }),
      /** publisher.go:44 — publisher_reads.go:626. */
      listPayouts: (cursor?: string | null) => c.get<T.PayoutList>(`/v1/payouts${qs({ cursor })}`),
      /** publisher.go:71 — publisher_reads.go:672. */
      getPayout: (id: string) => c.get<T.PayoutDetail>(`/v1/payouts/${enc(id)}`),
      /** publisher.go:45 — publisher_reads.go:869. */
      getPayoutAvailability: (siteId?: string | null) =>
        c.get<T.PayoutAvailability>(`/v1/payouts/availability${qs({ site_id: siteId })}`),
      /** report_snapshot.go:239 — publisher_earnings.go:45. */
      getEarnings: () => c.get<T.PublisherEarnings>("/v1/earnings"),
      /** GET /v1/earnings/promotional — promotions.go (0075). Never part of the payable balance. */
      getPromotionalEarnings: () => c.get<T.PromotionalEarnings>("/v1/earnings/promotional"),
      /** report_snapshot.go:240 — publisher_earnings.go:319. Window defaults to the last 30 days. */
      getEarningsBreakdown: (q: { from?: string; to?: string; siteId?: string | null } = {}) =>
        c.get<T.EarningsBreakdown>(`/v1/earnings/breakdown${qs({ from: q.from, to: q.to, site_id: q.siteId })}`),
      /** report_snapshot.go:241 — publisher_earnings.go:431. */
      getEarningsShadow: (q: { from?: string; to?: string; siteId?: string | null } = {}) =>
        c.get<T.EarningsShadow>(`/v1/earnings/shadow${qs({ from: q.from, to: q.to, site_id: q.siteId })}`),
      /** publisher.go:46 — publisher_reads.go:1168. */
      listDebtCases: (cursor?: string | null) => c.get<T.DebtCaseList>(`/v1/debt-cases${qs({ cursor })}`),
      /** publisher.go:47 — publisher_reads.go:1212. */
      getDebtCase: (id: string) => c.get<T.DebtCase>(`/v1/debt-cases/${enc(id)}`),
      /** publisher.go — publisher_commands.go handleAppealDebtCase (0073, G-44). No step-up: filing moves no money. */
      appealDebtCase: (id: string, req: { reason: string; evidence?: string }, opts?: RequestOptions) =>
        c.post<T.AppealDebtCaseResponse>(`/v1/debt-cases/${enc(id)}/appeal`, req, opts),

      // Publisher inventory (migration 0066, API-GAPS.md G-33/G-34/G-36/G-37):
      // publisher_inventory.go registerInventoryRoutes and
      // publisher_integration.go. Matrix rows (role_matrix.go:220-231): every
      // read is `view`, every write `edit_draft`.
      /** publisher_inventory.go:204-225. */
      getDeclaredTools: (siteId: string) => c.get<T.DeclaredToolsView>(`/v1/sites/${enc(siteId)}/declared-tools`),
      /** publisher_inventory.go:82-111 — 422 invalid_tool_declaration {index?, field, reason}, duplicate_tool_name, unknown_category, too_many_tools {max: 50}, invalid_locale; 400 tools_required. */
      putDeclaredTools: (siteId: string, req: T.DeclareToolsRequest, opts?: RequestOptions) =>
        c.put<T.DeclareToolsResponse>(`/v1/sites/${enc(siteId)}/declared-tools`, req, opts),
      /** publisher_inventory.go:307-346. The site's own list; a slot's GET also carries site_exclusions. */
      getSiteExclusions: (siteId: string) => c.get<T.SiteExclusionsView>(`/v1/sites/${enc(siteId)}/exclusions`),
      /** publisher_inventory.go:238-281. Replaces all three lists; an omitted list is emptied. */
      putSiteExclusions: (siteId: string, req: T.PutExclusionsRequest, opts?: RequestOptions) =>
        c.put<T.PutExclusionsResponse>(`/v1/sites/${enc(siteId)}/exclusions`, req, opts),
      /** publisher_inventory.go:307-346. */
      getSlotExclusions: (slotId: string) => c.get<T.SlotExclusionsView>(`/v1/slots/${enc(slotId)}/exclusions`),
      /** publisher_inventory.go:238-281. */
      putSlotExclusions: (slotId: string, req: T.PutExclusionsRequest, opts?: RequestOptions) =>
        c.put<T.PutExclusionsResponse>(`/v1/slots/${enc(slotId)}/exclusions`, req, opts),
      /**
       * publisher_inventory.go:370-405 — 201. Only a pricing-model change is
       * reviewed; a tool-name-only change goes live at once. 422 no_change,
       * slot_tool_name_taken {slot_id}, invalid_tool_name, slot_retired; 400
       * pricing_model_or_tool_name_required.
       */
      createSlotVersion: (slotId: string, req: T.CreateSlotVersionRequest, opts?: RequestOptions) =>
        c.post<T.CreateSlotVersionResponse>(`/v1/slots/${enc(slotId)}/versions`, req, opts),
      /** publisher_inventory.go:411-439 — 422 slot_version_not_pending {status, withdrawn_reason}; 404 slot_version_not_found. */
      withdrawSlotVersion: (slotId: string, versionId: string, opts?: RequestOptions) =>
        c.post<T.WithdrawSlotVersionResponse>(`/v1/slots/${enc(slotId)}/versions/${enc(versionId)}/withdraw`, undefined, opts),
      /** publisher_inventory.go:444-468 — 422 slot_already_paused. */
      pauseSlot: (slotId: string, opts?: RequestOptions) => c.post<T.SlotStatusResponse>(`/v1/slots/${enc(slotId)}/pause`, undefined, opts),
      /** publisher_inventory.go:444-468 — 422 slot_not_paused {status}. */
      resumeSlot: (slotId: string, opts?: RequestOptions) => c.post<T.SlotStatusResponse>(`/v1/slots/${enc(slotId)}/resume`, undefined, opts),
      /** publisher_inventory.go:444-468 — 422 slot_retired {retired_at}. Permanent; withdraws a pending version. */
      retireSlot: (slotId: string, opts?: RequestOptions) => c.post<T.SlotStatusResponse>(`/v1/slots/${enc(slotId)}/retire`, undefined, opts),
      /** publisher_integration.go — a §10-style live report from the reporting replica. Read-only. */
      getSiteIntegration: (siteId: string) => c.get<T.SiteIntegration>(`/v1/sites/${enc(siteId)}/integration`),
    },

    // system_status.go registerSystemRoutes (0055).
    system: {
      /** system_status.go:83 — unauthenticated (G-46). */
      environment: () => c.get<T.EnvironmentResponse>("/v1/environment", { quiet: true }),
      /** system_status.go:86 — session required (G-48). */
      status: () => c.get<T.SystemStatus>("/v1/status"),
    },

    statements: {
      /** statements.go:40 */
      list: () => c.get<T.StatementList>("/v1/statements"),
      /** statements.go:41 — records a statement_viewed activity event. Lines carry G-47's dispute status. */
      get: (id: string) => c.get<T.StatementDetail>(`/v1/statements/${enc(id)}`),
      /** disputes.go:77, 345-407 (0063, G-28 drill-down). */
      lineEvents: (statementId: string, lineItemId: string) =>
        c.get<T.StatementLineEvents>(`/v1/statements/${enc(statementId)}/lines/${enc(lineItemId)}/events`),
    },

    // Organization: cmd/core-platform/org.go registerOrgRoutes (0062, :42-67).
    // Both party types. Writes are idempotent (runCampaignCommand) and answer
    // typed rejections: 403 insufficient_role / insufficient_authority /
    // authority_not_yet_active / mfa_step_up_required, 404 *_not_found, 422
    // otherwise (campaign_commands.go:219-245).
    org: {
      /** org.go:44 — any active member. */
      listMembers: () => c.get<T.OrgMemberList>("/v1/org/members"),
      /** org.go:45 — owner/admin (403 insufficient_role otherwise). */
      listInvitations: () => c.get<T.OrgInvitationList>("/v1/org/invitations"),
      /** org.go:46, 191-242 — 201. A privileged role needs fresh MFA (the SQL's mfa_step_up_required). */
      invite: (email: string, role: T.OrgRole, opts?: RequestOptions) =>
        c.post<T.CreateInvitationResponse>("/v1/org/invitations", { email, role }, opts),
      /** org.go:47, 248-272. */
      revokeInvitation: (invitationId: string, opts?: RequestOptions) =>
        c.delete<T.RevokeInvitationResponse>(`/v1/org/invitations/${enc(invitationId)}`, undefined, opts),
      /**
       * org.go:49, 280-331 — pre-authentication: the token is the credential.
       * No session, CSRF or step-up; 201, 422 invitation_invalid, 400 password_policy {reason}.
       */
      acceptInvitation: (req: { token: string; password: string; recovery_contact: string }) =>
        c.post<T.AcceptInvitationResponse>("/v1/org/invitations/accept", req, { idempotencyKey: null, noStepUp: true, quiet: true }),
      /** org.go:50, 338-369 — 200, or 202 dual_control_pending for a privileged grant. */
      changeRole: (userId: string, role: T.OrgRole, opts?: RequestOptions) =>
        c.post<T.ChangeRoleResponse>(`/v1/org/members/${enc(userId)}/role`, { role }, opts),
      /** org.go:51, 380-408 — every unrevoked key the member created needs a disposition. */
      removeMember: (userId: string, dispositions: T.ApiKeyDisposition[], opts?: RequestOptions) =>
        c.delete<T.RemoveMemberResponse>(`/v1/org/members/${enc(userId)}`, { api_key_dispositions: dispositions }, opts),
      /** org.go:52, 415-441 — 202, owner only, fresh MFA. */
      transferOwnership: (toUserId: string, opts?: RequestOptions) =>
        c.post<T.OwnershipTransferResponse>("/v1/org/ownership-transfer", { to_user_id: toUserId }, opts),
    },

    /** org.go:55, 568-662 (G-07) — tiered by RLS (_audit_viewer_tier 0062:385-397, policy :399-411). */
    audit: {
      list: (q: { category?: string | null; actor?: string | null; cursor?: string | null }) =>
        c.get<T.AuditEventList>(`/v1/audit-events${qs({ category: q.category, actor: q.actor, cursor: q.cursor })}`),
    },

    /** org.go:58-60, 668-781 (G-08) — the caller's own inbox only (RLS, 0062:413-415). */
    notifications: {
      list: (q: { unread?: boolean; limit?: number; cursor?: string | null } = {}) =>
        c.get<T.NotificationList>(
          `/v1/notifications${qs({ unread: q.unread ? "true" : null, limit: q.limit ? String(q.limit) : null, cursor: q.cursor })}`,
        ),
      /** Naturally idempotent; another member's id is 404 notification_not_found. */
      markRead: (id: string) => c.post<T.MarkReadResponse>(`/v1/notifications/${enc(id)}/read`, undefined, noKey),
      markAllRead: () => c.post<T.MarkAllReadResponse>("/v1/notifications/read-all", undefined, noKey),
    },

    /** org.go:64-66, 447-515 (G-09). Approve is RequireRecentMFA; cancel never needs it. */
    approvals: {
      list: (status: "open" | "closed" | "all", cursor?: string | null) =>
        c.get<T.DualControlRequestList>(`/v1/dual-control-requests${qs({ status, cursor })}`),
      approve: (requestId: string, opts?: RequestOptions) =>
        c.post<T.ApproveRequestResponse>(`/v1/dual-control-requests/${enc(requestId)}/approve`, undefined, opts),
      cancel: (requestId: string, opts?: RequestOptions) =>
        c.post<T.CancelRequestResponse>(`/v1/dual-control-requests/${enc(requestId)}/cancel`, undefined, opts),
    },

    // Payout destination (G-42): account_lifecycle.go registerAccountLifecycleRoutes
    // (0065, :41-53). Publisher only (requirePartyType, 403 wrong_party_type).
    // Role matrix (role_matrix.go:218-220): the read is `view` (any member);
    // the change and the onboarding link are `payout_refund` (owner or
    // payout_approver), both RequireRecentMFA.
    payoutDestination: {
      /** account_lifecycle.go:44, 59-65 — get_payout_destination (0065:661-686). */
      get: () => c.get<T.PayoutDestinationView>("/v1/payout-destination"),
      /**
       * account_lifecycle.go:45-46, 74-129 — 202. The key is required (400
       * idempotency_key_required) and also scopes the Stripe account creation.
       */
      request: (opts?: RequestOptions) => c.post<T.RequestPayoutDestinationResponse>("/v1/payout-destination", undefined, opts),
      /** account_lifecycle.go:47-48, 148-170 — a fresh link for the pending version (else the current one); not replayed. */
      onboardingLink: () =>
        c.post<T.PayoutDestinationOnboardingLink>("/v1/payout-destination/onboarding-link", undefined, noKey),
    },

    // Account closure and export (G-30 advertiser, G-45 publisher):
    // account_lifecycle.go:50-52, 186-251. Role matrix (role_matrix.go:221-223):
    // the read and the export are `members` (owner or admin), closing is
    // `close_account` (owner only).
    account: {
      /** account_lifecycle.go:50, 186-192 — get_account_closure (0065:1204-1237). Also served after closure. */
      getClosure: () => c.get<T.AccountClosure>("/v1/account/closure"),
      /** account_lifecycle.go:51, 199-225 — close_account (0065:1246-1380). RequireRecentMFA; idempotent. */
      close: (opts?: RequestOptions) => c.post<T.CloseAccountResponse>("/v1/account/closure", { confirm: true }, opts),
      /**
       * account_lifecycle.go:52, 227-251 — the JSON document itself, as an
       * attachment (vads-account-export-<party>.json). RequireRecentMFA;
       * 413 export_too_large {max_bytes, size_bytes} past 8 MiB.
       */
      exportRecords: () => c.getRaw("/v1/account/export"),
    },

    // The targeting category vocabulary (targeting.go:23-66; 0061). One
    // shared route (advertiser.go:42, requirePartyType(pool, "advertiser",
    // "publisher")): 0061's header calls it "ONE TARGETING VOCABULARY", and
    // migration 0066's declare_site_tools/set_inventory_exclusions validate
    // a publisher's tool categories and exclusions against the exact same
    // table, so both portals read it here.
    targeting: {
      /** advertiser.go:42 — targeting.go:35. */
      listCategories: () => c.get<T.TargetingCategoryList>("/v1/targeting/categories"),
    },

    // Statement-line disputes: cmd/core-platform/disputes.go registerDisputeRoutes
    // (0063, :66-92), advertiser or publisher session. Filing needs no step-up.
    disputes: {
      /** disputes.go:79, 316-321. */
      list: (cursor?: string | null) => c.get<T.DisputeList>(`/v1/disputes${qs({ cursor })}`),
      /** disputes.go:81, 212-223. */
      get: (id: string) => c.get<T.StatementDispute>(`/v1/disputes/${enc(id)}`),
      /** disputes.go:75, 100-149 — 201 {applied, result: "filed", dispute}. */
      file: (statementId: string, lineItemId: string, req: { reason: string; evidence_ids: string[] }, opts?: RequestOptions) =>
        c.post<T.FileDisputeResponse>(`/v1/statements/${enc(statementId)}/lines/${enc(lineItemId)}/disputes`, req, opts),
      /**
       * disputes.go:73, dispute_evidence.go:134-227 — multipart, one part
       * named "file". The same bytes again are the same evidence (200
       * "existing"), so a retry is safe even under a new key.
       */
      uploadEvidence: (file: File, opts?: RequestOptions) => {
        const form = new FormData();
        form.append("file", file);
        return c.upload<T.UploadEvidenceResponse>("/v1/evidence", form, opts);
      },
    },

    // The staff console: every /v1/internal/* route (platform_reviewer).
    // Every write carries the intent's idempotency key (body + header).
    // Since migration 0068 (G-55) every route this file lists honours it and
    // replays (runInternalCommand, internal_commands.go) — before 0068 only
    // disputes.go:177 and receivable_write_off.go:164 did; the review.go
    // site-tools/slot-version routes are the two still-untouched exceptions
    // (API-GAPS.md "Known remaining gaps"): no key, no replay there.
    // Step-up: only the routes wrapped in RequireRecentMFA answer 403
    // mfa_step_up_required; the interceptor handles it. Migration 0068 added
    // it to safety-finding create/confirm/resolve/suspension-lift,
    // breaker trip/close, debt adjudicate/appeal-decide, write-off and
    // provider-operation decisions (StepUpNote required= in each page).
    staff: {
      review: {
        /** review.go:36, review_queue.go:35-198 — oldest first, at most 200 per kind. */
        queue: (kind?: S.ReviewQueueKind) => c.get<S.ReviewQueue>(`/v1/internal/review-queue${qs({ kind })}`),
        /** review.go:26, 52-79 — always 200; applied:false is a refusal. No step-up. */
        config: (id: string, req: S.ReviewVersionRequest, opts?: RequestOptions) =>
          c.post<S.ReviewVersionResponse>(`/v1/internal/config-versions/${enc(id)}/review`, req, opts),
        /** review.go:28, 81-108 — always 200; applied:false is a refusal. No step-up. */
        creative: (id: string, req: S.ReviewVersionRequest, opts?: RequestOptions) =>
          c.post<S.ReviewVersionResponse>(`/v1/internal/creative-versions/${enc(id)}/review`, req, opts),
        /** review.go:38, review_queue.go:207-244 — RequireRecentMFA; 422 typed refusals. */
        decideAppeal: (id: string, req: S.DecideAppealRequest, opts?: RequestOptions) =>
          c.post<S.DecideAppealResponse>(`/v1/internal/appeals/${enc(id)}/decide`, req, opts),
        /**
         * review.go:47, review_inventory.go:134-162 (migration 0066, G-33
         * console). Not touched by 0068: no idempotency key, no step-up,
         * typed 422 via writeCampaignError.
         */
        siteTools: (id: string, req: S.ReviewSiteToolsRequest, opts?: RequestOptions) =>
          c.post<S.ReviewSiteToolsResponse>(`/v1/internal/site-tool-declarations/${enc(id)}/review`, req, opts),
        /** review.go:49, review_inventory.go:168-196 (migration 0066, G-34 console). Same as siteTools: no idempotency/step-up. */
        slotVersion: (id: string, req: S.ReviewSlotVersionRequest, opts?: RequestOptions) =>
          c.post<S.ReviewSlotVersionResponse>(`/v1/internal/slot-versions/${enc(id)}/review`, req, opts),
      },
      disputes: {
        /** disputes.go:84, 323-338 — open is oldest first; resolved/all newest first. */
        list: (state: "open" | "resolved" | "all", cursor?: string | null) =>
          c.get<S.InternalDisputeList>(`/v1/internal/disputes${qs({ state, cursor })}`),
        /** disputes.go:86, 225-234. */
        get: (id: string) => c.get<S.InternalDispute>(`/v1/internal/disputes/${enc(id)}`),
        /** disputes.go:88, 165-210 — RequireRecentMFA, idempotent (runCampaignCommand). */
        decide: (id: string, req: S.DecideDisputeRequest, opts?: RequestOptions) =>
          c.post<S.DecideDisputeResponse>(`/v1/internal/disputes/${enc(id)}/decide`, req, opts),
        /** disputes.go:90 — the clean evidence bytes, as an attachment. */
        evidenceUrl: (evidenceId: string) => `/v1/internal/evidence/${enc(evidenceId)}/content`,
      },
      safety: {
        /** safety.go:55-56, 69-114 (migration 0068, G-51). ?status=in_progress|resolved|all (default all). */
        list: (status?: "in_progress" | "resolved" | "all") =>
          c.get<S.SafetyFindingList>(`/v1/internal/safety-findings${qs({ status })}`),
        /** safety.go:57-58, 90-114 (migration 0068, G-51). ?active=true|false (omitted: every suspension). */
        suspensions: (active?: "true" | "false") =>
          c.get<S.SuspensionList>(`/v1/internal/suspensions${qs({ active })}`),
        /** safety.go:59-60, 124-171 — RequireRecentMFA, no replay (single-reviewer, immediate hold). */
        createFinding: (req: S.CreateSafetyFindingRequest, opts?: RequestOptions) =>
          c.post<S.CreateSafetyFindingResponse>("/v1/internal/safety-findings", req, opts),
        /**
         * safety.go:61-62, 179-231 (migration 0068). RequireRecentMFA,
         * idempotent. The second, distinct reviewer whose confirmation starts
         * the financial fan-out; a stale impact preview is a 422
         * result:"impact_changed" carrying previous_impact/current_impact —
         * re-send with acceptUpdatedImpact:true to proceed.
         */
        confirm: (suspensionId: string, notes: string, acceptUpdatedImpact?: boolean, opts?: RequestOptions) =>
          c.post<S.ConfirmSafetyFindingResponse>(
            `/v1/internal/safety-findings/${enc(suspensionId)}/confirm`,
            { notes, accept_updated_impact: !!acceptUpdatedImpact },
            opts,
          ),
        /** safety.go:63-64, 308-358 — RequireRecentMFA, idempotent. `id` is the finding's suspension id. */
        resolve: (suspensionId: string, notes: string, opts?: RequestOptions) =>
          c.post<S.ResolveSafetyFindingResponse>(`/v1/internal/safety-findings/${enc(suspensionId)}/resolve`, { notes }, opts),
        /**
         * safety.go:65-66, 256-301 (migration 0068's lift_suspension_with_approval).
         * RequireRecentMFA, idempotent. Dual-approved only for a CONFIRMED
         * safety finding (result:"awaiting_second_approval" + impact_of_lift);
         * every other suspension still lifts on one reviewer.
         */
        lift: (suspensionId: string, reason: string, opts?: RequestOptions) =>
          c.post<S.LiftSuspensionResponse>(`/v1/internal/suspensions/${enc(suspensionId)}/lift`, { reason }, opts),
      },
      breaker: {
        /** breaker.go:41-42, 50-61 (migration 0068, G-52). */
        get: () => c.get<S.CircuitBreakerDetail>("/v1/internal/circuit-breaker"),
        /** breaker.go:43-44, 68-102. RequireRecentMFA, idempotent. Still single-reviewer and immediate. */
        trip: (reason: string, opts?: RequestOptions) =>
          c.post<S.TripBreakerResponse>("/v1/internal/circuit-breaker/trip", { reason }, opts),
        /**
         * breaker.go:45-46, 104-130 (migration 0068's request_breaker_close).
         * RequireRecentMFA, idempotent. Dual-approved, with an impact
         * preview (`cpe_campaigns_affected`); result:"awaiting_second_approval"
         * until a second, distinct reviewer confirms.
         */
        close: (reason: string, opts?: RequestOptions) =>
          c.post<S.CloseBreakerResponse>("/v1/internal/circuit-breaker/close", { reason }, opts),
      },
      debtCases: {
        /**
         * debt_adjudication.go:48-49, 64-92 (migration 0068, G-49;
         * cursor round-trip fixed alongside publisher-readable targeting
         * categories). ?state=pending_review|appealed|all (default
         * pending_review; pending_review is oldest first, a queue; the
         * others newest first). next_cursor is the same opaque base64
         * string every other paginated list uses.
         */
        list: (state?: "pending_review" | "appealed" | "all", cursor?: string | null) =>
          c.get<S.StaffDebtCaseList>(`/v1/internal/publisher-debt-cases${qs({ state, cursor })}`),
        /** debt_adjudication.go:35, 47-104 — 404 case_not_found. Now also carries §5's exposure (G-56). */
        get: (id: string) => c.get<S.StaffDebtCase>(`/v1/internal/publisher-debt-cases/${enc(id)}`),
        /** debt_adjudication.go:37, 111-143. */
        recordEvidence: (id: string, req: S.DebtCaseEvidenceRequest, opts?: RequestOptions) =>
          c.post<S.DebtCaseDecisionResponse>(`/v1/internal/publisher-debt-cases/${enc(id)}/evidence`, req, opts),
        /** debt_adjudication.go:39, 150-177. */
        adjudicate: (id: string, req: S.DebtCaseDecisionRequest, opts?: RequestOptions) =>
          c.post<S.DebtCaseDecisionResponse>(`/v1/internal/publisher-debt-cases/${enc(id)}/adjudicate`, req, opts),
        /** debt_adjudication.go:41, 183-210. */
        fileAppeal: (id: string, grounds: string, opts?: RequestOptions) =>
          c.post<S.DebtCaseDecisionResponse>(`/v1/internal/publisher-debt-cases/${enc(id)}/appeal`, { grounds }, opts),
        /** debt_adjudication.go:43, 212-239. */
        decideAppeal: (id: string, req: S.DebtCaseDecisionRequest, opts?: RequestOptions) =>
          c.post<S.DebtCaseDecisionResponse>(`/v1/internal/publisher-debt-cases/${enc(id)}/appeal/decide`, req, opts),
      },
      receivables: {
        /**
         * receivable_write_off.go:58-59, 74-105 (migration 0068, G-50;
         * cursor round-trip fixed alongside publisher-readable targeting
         * categories). ?kind=advertiser_receivable|publisher_debt,
         * ?writable=true (only cases write-off can act on today — a
         * double-payment receivable is still listed, just never
         * writable). next_cursor is the same opaque base64 string every
         * other paginated list uses.
         */
        list: (kind?: "advertiser_receivable" | "publisher_debt", writable?: boolean, cursor?: string | null) =>
          c.get<S.InternalReceivableList>(`/v1/internal/receivables${qs({ kind, writable: writable ? "true" : null, cursor })}`),
        /** receivable_write_off.go:49, 71-137 — 404 case_not_found. Now also carries the 0067 collection history. */
        get: (kind: S.ReceivableKindPath, caseId: string) =>
          c.get<S.ReceivableOutstanding>(`/v1/internal/receivables/${kind}/${enc(caseId)}`),
        /** receivable_write_off.go:51, 150-187 — RequireRecentMFA (migration 0068); the body key is required and replays. */
        writeOff: (kind: S.ReceivableKindPath, caseId: string, req: S.WriteOffRequest, opts?: RequestOptions) =>
          c.post<S.WriteOffResponse>(`/v1/internal/receivables/${kind}/${enc(caseId)}/write-off`, req, opts),
      },
      providerOperations: {
        /** provider_operations.go:66-67, 261-272. */
        unresolved: () => c.get<S.ProviderOperationList>("/v1/internal/provider-operations/unresolved"),
        /** provider_operations.go:68-69, 244-259 (migration 0068, G-57). Any status, not just unresolved. 404 provider_operation_not_found. */
        get: (id: string) => c.get<S.ProviderOperation>(`/v1/internal/provider-operations/${enc(id)}`),
        /** provider_operations.go:70-71, 286-333 — RequireRecentMFA, idempotent, typed 422 (migration 0068; the old 409 string detail is gone). */
        decide: (id: string, req: S.ProviderDecisionRequest, opts?: RequestOptions) =>
          c.post<S.ProviderDecisionResponse>(`/v1/internal/provider-operations/${enc(id)}/decision`, req, opts),
      },
      clientMatrix: {
        /** client_matrix.go:57, 102-114. */
        list: () => c.get<S.ClientMatrixList>("/v1/internal/client-matrix"),
        /** client_matrix.go:59, 136-169 — RequireRecentMFA, 201. */
        create: (req: S.CreateClientMatrixRequest, opts?: RequestOptions) =>
          c.post<S.ClientMatrixCommandResponse>("/v1/internal/client-matrix", req, opts),
        /** client_matrix.go:61, 177-215 — RequireRecentMFA. */
        revalidate: (id: string, req: S.RevalidateClientMatrixRequest, opts?: RequestOptions) =>
          c.post<S.ClientMatrixCommandResponse>(`/v1/internal/client-matrix/${enc(id)}/validate`, req, opts),
        /**
         * client_matrix.go:80-81, 261-320 — RequireRecentMFA. Suspending
         * only: status:"validated" is refused here since migration 0069
         * (409 reinstatement_requires_dual_control) — use reinstate below.
         */
        setStatus: (id: string, req: S.SetClientMatrixStatusRequest, opts?: RequestOptions) =>
          c.post<S.ClientMatrixCommandResponse>(`/v1/internal/client-matrix/${enc(id)}/status`, req, opts),
        /**
         * client_matrix.go:82-83, 332-368 (migration 0069's
         * request_client_matrix_reinstatement) — RequireRecentMFA. Dual
         * control: the first platform_reviewer's call only casts a vote;
         * a second, distinct reviewer's call is what actually reinstates.
         */
        reinstate: (id: string, req: S.ReinstateClientMatrixRequest, opts?: RequestOptions) =>
          c.post<S.ReinstateClientMatrixResponse>(`/v1/internal/client-matrix/${enc(id)}/reinstate`, req, opts),
      },
      reconciliation: {
        /**
         * reconciliation.go (migration 0072): the last run, the aggregate
         * discrepancy, whether the ledger-wide payout halt is open, and the
         * override's funding picture. `override.state` is always
         * "not_implemented": the degraded-mode override is not built.
         */
        overview: () => c.get<S.ReconciliationOverview>("/v1/internal/reconciliation"),
        /** ?status=open|cleared|all (default open), newest first. */
        quarantines: (status?: "open" | "cleared" | "all") =>
          c.get<S.FinancialQuarantineList>(`/v1/internal/reconciliation/quarantines${qs({ status })}`),
        /** One quarantine with every discrepancy and vote. 404 quarantine_not_found. */
        quarantine: (id: string) => c.get<S.FinancialQuarantineDetail>(`/v1/internal/reconciliation/quarantines/${enc(id)}`),
        /** ?status=present|open|all (default open: not yet closed by a clear). */
        discrepancies: (status?: "present" | "open" | "all") =>
          c.get<S.ReconciliationDiscrepancyList>(`/v1/internal/reconciliation/discrepancies${qs({ status })}`),
        /**
         * clear_financial_quarantine — RequireRecentMFA, idempotent, dual
         * control: two distinct reviewers, the same resolution, the same
         * version (a stale expected_version is a 422 quarantine_changed).
         */
        clear: (id: string, req: S.ClearQuarantineRequest, opts?: RequestOptions) =>
          c.post<S.ClearQuarantineResponse>(`/v1/internal/reconciliation/quarantines/${enc(id)}/clear`, req, opts),
      },
      promotions: {
        /** promotions.go (0075): every promotion with its granted/outstanding/retired totals. */
        list: () => c.get<S.PromotionList>("/v1/internal/promotions"),
        /** RequireRecentMFA, idempotent (internal.create_promotion). 422 invalid_promotion {field}, promo_code_taken. */
        create: (req: S.CreatePromotionRequest, opts?: RequestOptions) =>
          c.post<S.PromotionCommandResponse>("/v1/internal/promotions", req, opts),
        /** RequireRecentMFA. Existing grants stay valid until they expire. */
        end: (id: string, reason: string, opts?: RequestOptions) =>
          c.post<S.PromotionCommandResponse>(`/v1/internal/promotions/${enc(id)}/end`, { reason }, opts),
        /** RequireRecentMFA, idempotent. 422 promo_limit_reached, amount_exceeds_promotion, promotion_ended, account_closed. */
        grant: (id: string, req: S.GrantPromoCreditRequest, opts?: RequestOptions) =>
          c.post<S.GrantPromoCreditResponse>(`/v1/internal/promotions/${enc(id)}/grants`, req, opts),
        /** Outstanding promo credit and promotional earnings liability, per currency. */
        liability: () => c.get<S.PromoLiabilityReport>("/v1/internal/promotions/liability"),
        /** The pool's balance, what a funding would convert now, and every funding. */
        pool: () => c.get<S.PromoPool>("/v1/internal/promotions/pool"),
        /** Reviewer 1 (RequireRecentMFA): 202 awaiting_second_approval. */
        proposeFunding: (req: S.ProposePoolFundingRequest, opts?: RequestOptions) =>
          c.post<S.PoolFundingResponse>("/v1/internal/promotions/pool/fundings", req, opts),
        /**
         * A DIFFERENT reviewer (RequireRecentMFA) confirming the amount they
         * were shown: funds the pool and converts. 422 second_reviewer_required,
         * amount_mismatch, proposal_expired; already_executed is a no-op.
         */
        approveFunding: (id: string, expectedAmount: string, opts?: RequestOptions) =>
          c.post<S.PoolFundingResponse>(`/v1/internal/promotions/pool/fundings/${enc(id)}/approve`, { expected_amount: expectedAmount }, opts),
      },
      siteRateLimit: {
        /** site_rate_limit.go (migration 0077, PLAN.md §9). No step-up: a read changes nothing. */
        get: (siteId: string) => c.get<S.SiteRateLimitResponse>(`/v1/internal/sites/${enc(siteId)}/rate-limit`),
        /** RequireRecentMFA, idempotent (internal.set_site_decision_rate_limit). Set (rate_per_sec + burst) or clear. */
        set: (siteId: string, req: S.SetSiteRateLimitRequest, opts?: RequestOptions) =>
          c.put<S.SetSiteRateLimitResponse>(`/v1/internal/sites/${enc(siteId)}/rate-limit`, req, opts),
      },
    },
  };
}

export type Endpoints = ReturnType<typeof endpoints>;
export const apiEndpoints = endpoints();
